import { ReactNode, useEffect } from 'react'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { ConfigProvider, theme as antdTheme } from 'antd'
import enUS from 'antd/locale/en_US'
import arEG from 'antd/locale/ar_EG'
import { LocaleProvider, useLocale } from '@/i18n/LocaleProvider'
import { ThemeProvider, useTheme } from '@/contexts/ThemeContext'
import { ensureLeapBridgeInit } from '@/services/scanner/leapNativeBridge'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 30_000 },
  },
})

function AntdLocale({ children }: { children: ReactNode }) {
  const { locale } = useLocale()
  const { isDark } = useTheme()

  return (
    <ConfigProvider
      locale={locale === 'ar' ? arEG : enUS}
      direction={locale === 'ar' ? 'rtl' : 'ltr'}
      theme={{
        algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
        token: {
          colorPrimary: '#af0000',
          fontFamily: "'Poppins', ui-sans-serif, system-ui, sans-serif",
          borderRadius: 12,
          colorBgLayout: isDark ? '#000000' : '#f4f6f8',
          colorBgContainer: isDark ? '#0a0a0a' : '#ffffff',
          colorBorder: isDark ? '#222222' : '#e0e0e0',
          colorText: isDark ? '#f5f5f5' : '#1a1a2e',
          colorTextSecondary: isDark ? '#a1a1aa' : '#6c757d',
        },
        components: {
          Card: {
            borderRadiusLG: 16,
          },
          Table: {
            headerBg: isDark ? '#111111' : '#fafafa',
          },
          Button: {
            borderRadius: 14,
            controlHeight: 44,
            fontWeight: 800,
          },
        },
      }}
    >
      {children}
    </ConfigProvider>
  )
}

export function AppProviders({ children }: { children: ReactNode }) {
  useEffect(() => {
    ensureLeapBridgeInit()
  }, [])

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <LocaleProvider>
          <AntdLocale>
            <BrowserRouter>{children}</BrowserRouter>
          </AntdLocale>
        </LocaleProvider>
      </ThemeProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  )
}
